import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import { InfoCircleOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Radio, Switch, Tooltip } from 'antd';
import { Select } from '../../formControls/Select/Select';

import { stepperInfo } from '../../forms/CreateRide/stepperInfo';
import { getWheelchairQuestions } from '../../forms/mock';
import { LodashHelper } from '../../../utils';
import { getOrgUberHealthEnabled } from '../../../utils/uberHealthHelper';
import {
  VEHICLE_OPTIONS,
  VEHICLE_TYPES,
  UBER_TEXT,
  CAR_SEAT_OR_BOOSTER_VALIDATION_MESSAGE,
  REQUIRED_ADDITIONAL_OPTION_FOR_VEHICLE_TYPE_OVER_250_LBS,
  VEHICLE_TYPES_BARIATRIC_HEIGHT,
  VEHICLE_TYPES_BARIATRIC,
} from '../../../constants/constants';
import { vehicleOptionsDictionary } from '../../../dictionaries';
import stairsIcon from '../../../assets/svgs/stairs.svg';
import dischargeIcon from '../../../assets/svgs/dischargeIcon.svg';
import carBoosterIcon from '../../../assets/svgs/CarBoosterSeatIcon.svg';
import infoIcon from '../../../assets/svgs/infoIcon.svg';
import styles from './quickScheduleNew.module.css';

const FieldLabel = ({ children, required }) => (
  <label className={`${styles.fieldLabel} ${required ? styles.required : ''}`}>
    {children}
  </label>
);

const fieldError = (errors, path) => {
  const parts = path.split('.');
  let err = errors;
  for (const p of parts) {
    err = err?.[p];
  }
  if (!err || err === '*required') return null;
  return typeof err === 'string' ? err : null;
};

const InfoAlert = ({ message }) => (
  <div className={styles.alertInfo}>
    <img src={infoIcon} alt="" className={styles.alertInfoIcon} aria-hidden />
    <span>{message}</span>
  </div>
);

const STEP_TWO_RADIO_BOOLEAN_FIELDS = [
  'hasStairs',
  'needsCarSeat',
  'dischargeFromFacility',
];

const STEP_TWO_WHEELCHAIR_BOOLEAN_FIELDS = [
  'isWheelchairNeeded',
  'hasMotorizedWheelchair',
];

const isWheelchairVehicleType = (vehicleType) =>
  vehicleType === VEHICLE_TYPES.WHEEL_CHAIR_BOUND_BARIATRIC ||
  vehicleType === VEHICLE_TYPES.WHEEL_CHAIR_BOUND_NON_BARIATRIC;

export const normalizeStepTwoBooleans = (values, vehicleType) => {
  if (!values || vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE) {
    return values;
  }

  const next = { ...values };

  STEP_TWO_RADIO_BOOLEAN_FIELDS.forEach((field) => {
    if (next[field] === null || next[field] === undefined) {
      next[field] = false;
    }
  });

  if (isWheelchairVehicleType(vehicleType)) {
    STEP_TWO_WHEELCHAIR_BOOLEAN_FIELDS.forEach((field) => {
      if (next[field] === null || next[field] === undefined) {
        next[field] = false;
      }
    });
  }

  if (next.needsCarSeat === true) {
    if (next.needsAdditionalCarSeat === null || next.needsAdditionalCarSeat === undefined) {
      next.needsAdditionalCarSeat = false;
    }
    if (next.needsBoosterSeat === null || next.needsBoosterSeat === undefined) {
      next.needsBoosterSeat = false;
    }
  }

  return next;
};

const stepTwoHasUnsetRadioBooleans = (values, vehicleType) => {
  if (!vehicleType || vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE) {
    return false;
  }

  const hasUnsetCommon = STEP_TWO_RADIO_BOOLEAN_FIELDS.some(
    (field) => values[field] === null || values[field] === undefined,
  );

  const hasUnsetWheelchair =
    isWheelchairVehicleType(vehicleType) &&
    STEP_TWO_WHEELCHAIR_BOOLEAN_FIELDS.some(
      (field) => values[field] === null || values[field] === undefined,
    );

  return hasUnsetCommon || hasUnsetWheelchair;
};

const getWarningMessageByMinTime = (minTime, chosenVehicleType) => {
  if (
    minTime &&
    chosenVehicleType &&
    minTime[chosenVehicleType] &&
    minTime[chosenVehicleType].hours > 0
  ) {
    return `This ride type will take on average a minimum of ${minTime[chosenVehicleType].hours} hour(s) ${minTime[chosenVehicleType].minutes} minute(s) for the rider to be picked up.`;
  }
  if (minTime && chosenVehicleType && minTime[chosenVehicleType]) {
    return `This ride type will take on average a minimum of ${minTime[chosenVehicleType].minutes} minute(s) for the rider to be picked up.`;
  }
  return '';
};

const ToggleRow = ({ icon, text, checked, onChange, disabled, showInfo }) => (
  <div className={styles.toggleRow}>
    <div className={styles.toggleRowLeft}>
      {icon}
      <span className={styles.toggleRowText}>
        {text}
        {showInfo && (
          <img
            src={infoIcon}
            alt=""
            style={{ marginLeft: 6, verticalAlign: 'middle', width: 14, height: 14 }}
            aria-hidden
          />
        )}
      </span>
    </div>
    <Switch checked={Boolean(checked)} onChange={onChange} disabled={disabled} />
  </div>
);

const GLOBAL_SEAT_MAX = 2;
const PER_FIELD_SEAT_MAX = 2;

/** Matches legacy NumericInput: plus respects global max; minus only disabled at 0. */
const getSeatCounterState = (count, vehicleDisabled, totalSeats) => {
  const seatCount = Number(count) || 0;
  const globalMaxReached = totalSeats >= GLOBAL_SEAT_MAX;

  return {
    disableDecrement: vehicleDisabled || seatCount <= 0,
    disableIncrement:
      vehicleDisabled || globalMaxReached || seatCount >= PER_FIELD_SEAT_MAX,
  };
};

const Counter = ({ value, onChange, min = 0, max = 2, disableDecrement, disableIncrement }) => (
  <div className={styles.counter}>
    <Button
      type="default"
      className={`${styles.counterBtn} ${!disableDecrement ? styles.counterBtnActive : ''}`}
      icon={<MinusOutlined />}
      disabled={disableDecrement}
      onClick={() => onChange(Math.max(min, Number(value) - 1))}
    />
    <span className={styles.counterValue}>{value}</span>
    <Button
      type="default"
      className={styles.counterBtn}
      icon={<PlusOutlined />}
      disabled={disableIncrement}
      onClick={() => onChange(Math.min(max, Number(value) + 1))}
    />
  </div>
);

const SeatTable = ({
  title,
  riderCount,
  driverCount,
  onRiderChange,
  onDriverChange,
  vehicleDisabled,
  totalSeats,
}) => {
  const riderState = getSeatCounterState(riderCount, vehicleDisabled, totalSeats);
  const driverState = getSeatCounterState(driverCount, vehicleDisabled, totalSeats);

  return (
    <div className={styles.seatTable}>
      <div className={styles.seatTableHeader}>{title}</div>
      <div className={styles.seatTableRow}>
        <span>Provided by Rider</span>
        <Counter
          value={riderCount}
          onChange={onRiderChange}
          disableDecrement={riderState.disableDecrement}
          disableIncrement={riderState.disableIncrement}
        />
      </div>
      <div className={styles.seatTableRow}>
        <span>Provided by Driver</span>
        <Counter
          value={driverCount}
          onChange={onDriverChange}
          disableDecrement={driverState.disableDecrement}
          disableIncrement={driverState.disableIncrement}
        />
      </div>
    </div>
  );
};

const RadioQuestionRow = ({ title, name, value, onChange, disabled }) => (
  <div className={`${styles.toggleRow} ${styles.radioRow}`}>
    <span className={styles.toggleRowText}>{title}</span>
    <Radio.Group
      className={styles.radioGroup}
      value={value ?? false}
      disabled={disabled}
      onChange={(e) => onChange(name, e.target.value)}
    >
      <Radio value={true}>Yes</Radio>
      <Radio value={false}>No</Radio>
    </Radio.Group>
  </div>
);

const HeightFields = ({ heightInches, disabled, onHeightChange }) => {
  const [feet, setFeet] = useState('');
  const [inches, setInches] = useState('');

  useEffect(() => {
    if (heightInches == null || heightInches === '') {
      setFeet('');
      setInches('');
      return;
    }
    const total = Number(heightInches);
    if (Number.isNaN(total)) return;
    setFeet(String(Math.floor(total / 12)));
    setInches(String(total % 12));
  }, [heightInches]);

  const updateHeight = useCallback(
    (nextFeet, nextInches) => {
      const f = nextFeet === '' ? 0 : Number(nextFeet);
      const i = nextInches === '' ? 0 : Number(nextInches);
      if (nextFeet === '' && nextInches === '') {
        onHeightChange(null);
        return;
      }
      onHeightChange(Number(f * 12) + Number(i));
    },
    [onHeightChange],
  );

  const handleFeetChange = (e) => {
    const val = e.target.value;
    if (val !== '' && (Number(val) > 12 || String(val).length > 2)) return;
    setFeet(val);
    updateHeight(val, inches);
  };

  const handleInchesChange = (e) => {
    const val = e.target.value;
    if (val !== '' && (Number(val) > 11 || String(val).length > 2)) return;
    setInches(val);
    updateHeight(feet, val);
  };

  return (
    <div className={styles.bariatricHeightBlock}>
      <FieldLabel required>Height, (Feet/Inches)</FieldLabel>
      <div className={styles.bariatricHeightRow}>
        <div className={styles.bariatricHeightField}>
          <span className={styles.bariatricHeightLabel}>Feet</span>
          <Input
            className={styles.inputFilled}
            type="number"
            min={0}
            max={12}
            value={feet}
            disabled={disabled}
            onChange={handleFeetChange}
            placeholder="0"
          />
        </div>
        <div className={styles.bariatricHeightField}>
          <span className={styles.bariatricHeightLabel}>Inches</span>
          <Input
            className={styles.inputFilled}
            type="number"
            min={0}
            max={11}
            value={inches}
            disabled={disabled}
            onChange={handleInchesChange}
            placeholder="0"
          />
        </div>
      </div>
    </div>
  );
};

function getInitialStepTwoValues(draft) {
  if (draft?.step2) {
    return normalizeStepTwoBooleans(
      {
        ...draft.step2,
        weight: draft.step2.weight ?? null,
        height: draft.step2.height ?? null,
      },
      draft.step2.vehicleType,
    );
  }
  return normalizeStepTwoBooleans(stepperInfo[1].values, stepperInfo[1].values.vehicleType);
}

const TripDetailsPanel = ({
  draft,
  stepOneValues,
  rideOptions = [],
  minTimeFromRideRequestToPickup,
  isUberEnabled,
  disabledValues,
  onValuesChange,
  onValidChange,
  onSeatValidationChange,
}) => {
  const isUberEligibleFromAuth = useSelector((state) => state?.authorization?.isUberEligible);
  const user = useSelector((state) => state?.authorization?.user);
  const hydratedRef = useRef(null);
  const [showCarBoosterComp, setShowCarBoosterComp] = useState(false);

  const isUberEligibleForOrg = getOrgUberHealthEnabled({
    isUberEnabled,
    isUberEligibleFromAuth,
    user,
  });

  const vehicleDisabled = Boolean(disabledValues?.vehicleInformation);

  const formik = useFormik({
    enableReinitialize: false,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: normalizeStepTwoBooleans(stepperInfo[1].values, stepperInfo[1].values.vehicleType),
    validationSchema: stepperInfo[1].schema(),
    onSubmit: () => {},
  });

  const { values, errors, isValid, setFieldValue, setValues, validateForm, touched, setFieldTouched } =
    formik;

  const isUberRide = values?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;

  const showNonUberVehicleQuestions = Boolean(
    values?.vehicleType && !isUberRide,
  );

  const totalSeats = useMemo(
    () =>
      (values.carSeatByDriver || 0) +
      (values.carSeatByRider || 0) +
      (values.boosterSeatByDriver || 0) +
      (values.boosterSeatByRider || 0),
    [
      values.carSeatByDriver,
      values.carSeatByRider,
      values.boosterSeatByDriver,
      values.boosterSeatByRider,
    ],
  );

  const filteredVehicleOptions = useMemo(() => {
    let opts = !isUberEligibleForOrg
      ? VEHICLE_OPTIONS.filter((option) => option.value !== VEHICLE_TYPES.UBER_AMBULATORY_RIDE)
      : [...VEHICLE_OPTIONS];
    if (
      values?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE &&
      !opts.some((o) => o.value === VEHICLE_TYPES.UBER_AMBULATORY_RIDE)
    ) {
      opts = [
        ...opts,
        {
          id: 6,
          title: vehicleOptionsDictionary.uberAmbulatoryRide,
          value: VEHICLE_TYPES.UBER_AMBULATORY_RIDE,
        },
      ];
    }
    return opts;
  }, [isUberEligibleForOrg, values?.vehicleType]);

  const wheelchairOnlyQuestions = useMemo(
    () =>
      getWheelchairQuestions({
        vehicleType: values?.vehicleType,
        needsCarSeat: values?.needsCarSeat,
      }).filter((q) => !['hasStairs', 'dischargeFromFacility', 'needsCarSeat'].includes(q.name)),
    [values?.vehicleType, values?.needsCarSeat],
  );

  const warningMessage = getWarningMessageByMinTime(
    minTimeFromRideRequestToPickup,
    values?.vehicleType,
  );

  const showBariatricWeight =
    !isUberRide && VEHICLE_TYPES_BARIATRIC.includes(values?.vehicleType);
  const showBariatricHeight =
    !isUberRide && VEHICLE_TYPES_BARIATRIC_HEIGHT.includes(values?.vehicleType);

  const showSeatTypeValidationError = Boolean(
    showCarBoosterComp && values?.needsCarSeat && totalSeats === 0,
  );

  useEffect(() => {
    if (isUberRide) {
      setShowCarBoosterComp(false);
      return;
    }
    setShowCarBoosterComp(Boolean(values?.needsCarSeat));
  }, [isUberRide, values?.needsCarSeat]);

  useEffect(() => {
    if (values.vehicleType !== VEHICLE_TYPES.UBER_AMBULATORY_RIDE) return;

    const normalizedValues = {
      ...values,
      hasStairs: false,
      needsCarSeat: false,
      dischargeFromFacility: false,
      needsAdditionalCarSeat: false,
      needsBoosterSeat: false,
      isWheelchairNeeded: null,
      hasMotorizedWheelchair: null,
      weight: null,
      height: null,
    };

    const hasDifferences =
      values.hasStairs !== normalizedValues.hasStairs ||
      values.needsCarSeat !== normalizedValues.needsCarSeat ||
      values.dischargeFromFacility !== normalizedValues.dischargeFromFacility ||
      values.needsAdditionalCarSeat !== normalizedValues.needsAdditionalCarSeat ||
      values.needsBoosterSeat !== normalizedValues.needsBoosterSeat ||
      values.isWheelchairNeeded !== normalizedValues.isWheelchairNeeded ||
      values.hasMotorizedWheelchair !== normalizedValues.hasMotorizedWheelchair ||
      values.weight !== normalizedValues.weight ||
      values.height !== normalizedValues.height;

    if (hasDifferences) {
      void setValues(normalizedValues, false);
    }
  }, [values, setValues]);

  useEffect(() => {
    if (!values?.vehicleType || isUberRide) return;
    if (!stepTwoHasUnsetRadioBooleans(values, values.vehicleType)) return;

    const normalized = normalizeStepTwoBooleans(values, values.vehicleType);
    void (async () => {
      await setValues(normalized, false);
      await validateForm();
    })();
  }, [
    values?.vehicleType,
    values?.hasStairs,
    values?.needsCarSeat,
    values?.dischargeFromFacility,
    values?.isWheelchairNeeded,
    values?.hasMotorizedWheelchair,
    isUberRide,
    setValues,
    validateForm,
  ]);

  useEffect(() => {
    const next = getInitialStepTwoValues(draft);
    if (LodashHelper.isEqual(hydratedRef.current, next)) return;
    hydratedRef.current = next;
    void (async () => {
      await setValues(next);
      await validateForm();
    })();
  }, [draft?.step2, setValues, validateForm]);

  useEffect(() => {
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  useEffect(() => {
    onValidChange?.(isUberRide || (isValid && !showSeatTypeValidationError));
  }, [isUberRide, isValid, showSeatTypeValidationError, onValidChange]);

  useEffect(() => {
    onSeatValidationChange?.({
      needsCarSeat: Boolean(values?.needsCarSeat),
      totalSeats,
      showSeatTypeValidationError,
    });
  }, [values?.needsCarSeat, totalSeats, showSeatTypeValidationError, onSeatValidationChange]);

  const handleVehicleTypeChange = useCallback(
    (value) => {
      const copiedValues = { ...values };
      const overLbsOption = rideOptions.find(
        (option) => option.value === REQUIRED_ADDITIONAL_OPTION_FOR_VEHICLE_TYPE_OVER_250_LBS,
      );

      const optionsWithoutOverLbs =
        copiedValues?.rideOptions?.filter((option) => option.id !== overLbsOption?.id) || [];

      const optionsWithOverLbs = overLbsOption
        ? [...optionsWithoutOverLbs, overLbsOption]
        : copiedValues?.rideOptions;

      switch (value) {
        case VEHICLE_TYPES.WHEEL_CHAIR_BOUND_BARIATRIC:
        case VEHICLE_TYPES.STRETCHER_BOUND_BARIATRIC:
          void setValues(
            normalizeStepTwoBooleans(
              {
                ...copiedValues,
                rideOptions: optionsWithOverLbs,
                weight: values?.weight,
                vehicleType: value,
              },
              value,
            ),
          );
          break;

        case VEHICLE_TYPES.WHEEL_CHAIR_BOUND_NON_BARIATRIC:
          void setValues(
            normalizeStepTwoBooleans(
              {
                ...copiedValues,
                rideOptions: optionsWithoutOverLbs,
                weight: null,
                vehicleType: value,
              },
              value,
            ),
          );
          break;

        case VEHICLE_TYPES.UBER_AMBULATORY_RIDE:
          void setValues({
            ...copiedValues,
            rideOptions: optionsWithoutOverLbs,
            vehicleType: value,
            hasStairs: false,
            needsCarSeat: false,
            dischargeFromFacility: false,
            needsAdditionalCarSeat: false,
            needsBoosterSeat: false,
            isWheelchairNeeded: null,
            hasMotorizedWheelchair: null,
            weight: null,
            height: null,
            carSeatByDriver: 0,
            carSeatByRider: 0,
            boosterSeatByDriver: 0,
            boosterSeatByRider: 0,
          });
          break;

        default: {
          const next = { ...copiedValues, rideOptions: optionsWithoutOverLbs, vehicleType: value };
          delete next.isWheelchairNeeded;
          delete next.hasMotorizedWheelchair;
          void setValues(
            normalizeStepTwoBooleans(
              {
                ...next,
                weight: values?.weight,
              },
              value,
            ),
          );
        }
      }
      void setFieldTouched('vehicleType', true);
    },
    [rideOptions, setFieldTouched, setValues, values],
  );

  const handleNeedsCarSeatChange = useCallback(
    async (needsSeat) => {
      if (!needsSeat) {
        setShowCarBoosterComp(false);
        await setFieldValue('needsAdditionalCarSeat', false);
        await setFieldValue('needsBoosterSeat', false);
        await setFieldValue('boosterSeatByDriver', 0);
        await setFieldValue('boosterSeatByRider', 0);
        await setFieldValue('carSeatByDriver', 0);
        await setFieldValue('carSeatByRider', 0);
        await setFieldValue('riderCarSeat', undefined);
        await setFieldValue('driverCarSeat', undefined);
        await setFieldValue('riderBoosterSeat', undefined);
        await setFieldValue('driverBoosterSeat', undefined);
      } else {
        setShowCarBoosterComp(true);
        await setFieldValue('needsAdditionalCarSeat', false);
        await setFieldValue('needsBoosterSeat', false);
      }
      await setFieldValue('needsCarSeat', needsSeat);
      await setFieldTouched('needsCarSeat', true);
      await validateForm();
    },
    [setFieldTouched, setFieldValue, validateForm],
  );

  const handleBooleanField = useCallback(
    async (name, val) => {
      await setFieldValue(name, val);
      await setFieldTouched(name, true);
    },
    [setFieldTouched, setFieldValue],
  );

  const handleSeatCountChange = useCallback(
    async (name, count) => {
      await setFieldValue(name, count);

      const carSeatByRider = name === 'carSeatByRider' ? count : values.carSeatByRider;
      const carSeatByDriver = name === 'carSeatByDriver' ? count : values.carSeatByDriver;
      const boosterSeatByRider = name === 'boosterSeatByRider' ? count : values.boosterSeatByRider;
      const boosterSeatByDriver =
        name === 'boosterSeatByDriver' ? count : values.boosterSeatByDriver;

      if (name === 'carSeatByRider' || name === 'carSeatByDriver') {
        await setFieldValue('needsAdditionalCarSeat', carSeatByRider > 0 || carSeatByDriver > 0);
      }

      if (name === 'boosterSeatByRider' || name === 'boosterSeatByDriver') {
        await setFieldValue('needsBoosterSeat', boosterSeatByRider > 0 || boosterSeatByDriver > 0);
      }

      await validateForm();
    },
    [setFieldValue, values, validateForm],
  );

  const vehicleSelect = (
    <FormikProvider value={formik}>
      <Select
        name="vehicleType"
        options={filteredVehicleOptions}
        placeholder="Pick one"
        value={values?.vehicleType || undefined}
        disabled={vehicleDisabled}
        customErrorMessage
        whenFieldDisabledShowTooltip={vehicleDisabled}
        myClassName={`${styles.inputFilled} ${styles.selectField}`}
        onChange={({ value }) => handleVehicleTypeChange(value)}
        getPopupContainer={(trigger) => trigger?.closest?.('.ant-modal-container, .ant-modal-content') || document.body}
      />
    </FormikProvider>
  );

  return (
    <>
      <div className={styles.formRowFull}>
        <FieldLabel required>Vehicle Type</FieldLabel>
        {vehicleDisabled ? (
          <Tooltip title="This field cannot be edited">
            <span className={styles.fieldTooltipWrap}>{vehicleSelect}</span>
          </Tooltip>
        ) : (
          vehicleSelect
        )}
        {touched?.vehicleType && fieldError(errors, 'vehicleType') && (
          <div className={styles.fieldError} role="alert">
            {fieldError(errors, 'vehicleType')}
          </div>
        )}
      </div>

      {isUberRide && (
        <div className={styles.uberNotification}>
          <div className={styles.uberNotificationBadge}>
            <InfoCircleOutlined className={styles.uberNotificationIcon} />
            <span>Important Info</span>
          </div>
          <div
            className={styles.uberNotificationContent}
            dangerouslySetInnerHTML={{ __html: UBER_TEXT.UBER_HEALTH_RIDE_DESCRIPTION }}
          />
        </div>
      )}

      {!isUberRide &&
        values?.vehicleType &&
        minTimeFromRideRequestToPickup?.[values.vehicleType] &&
        warningMessage && <InfoAlert message={warningMessage} />}

      {showBariatricWeight && (
        <div className={styles.formRowFull}>
          <FieldLabel required>Weight, lb(s)</FieldLabel>
          <Input
            className={styles.inputFilled}
            type="number"
            min={250}
            max={999}
            placeholder="Weight"
            value={values?.weight ?? ''}
            disabled={vehicleDisabled}
            onChange={(e) => {
              const raw = e.target.value;
              void setFieldValue('weight', raw === '' ? null : Number(raw));
            }}
            onBlur={() => setFieldTouched('weight', true)}
          />
          {touched?.weight && fieldError(errors, 'weight') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'weight')}
            </div>
          )}
        </div>
      )}

      {showBariatricHeight && (
        <>
          <HeightFields
            heightInches={values?.height}
            disabled={vehicleDisabled}
            onHeightChange={(h) => setFieldValue('height', h)}
          />
          {touched?.height && fieldError(errors, 'height') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'height')}
            </div>
          )}
        </>
      )}

      {showNonUberVehicleQuestions && (
        <>
          <ToggleRow
            icon={<img src={stairsIcon} alt="" className={styles.toggleRowIcon} />}
            text="Will there be stairs in or outside of the home?"
            checked={values?.hasStairs ?? false}
            disabled={vehicleDisabled}
            onChange={(checked) => handleBooleanField('hasStairs', checked)}
          />
          {touched?.hasStairs && fieldError(errors, 'hasStairs') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'hasStairs')}
            </div>
          )}

          <ToggleRow
            icon={<img src={dischargeIcon} alt="" className={styles.toggleRowIcon} />}
            text="Is rider being picked up as a result of a discharge from a facility?"
            checked={values?.dischargeFromFacility ?? false}
            disabled={vehicleDisabled}
            onChange={(checked) => handleBooleanField('dischargeFromFacility', checked)}
          />
          {touched?.dischargeFromFacility && fieldError(errors, 'dischargeFromFacility') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'dischargeFromFacility')}
            </div>
          )}

          {wheelchairOnlyQuestions.map((question) => (
            <React.Fragment key={question.name}>
              <RadioQuestionRow
                title={question.title}
                name={question.name}
                value={values?.[question.name]}
                disabled={vehicleDisabled}
                onChange={handleBooleanField}
              />
              {touched?.[question.name] && fieldError(errors, question.name) && (
                <div className={styles.fieldError} role="alert">
                  {fieldError(errors, question.name)}
                </div>
              )}
            </React.Fragment>
          ))}

          <div className={`${styles.toggleRow} ${styles.radioRow}`}>
            <div className={styles.toggleRowLeft}>
              <img
                src={carBoosterIcon}
                alt=""
                className={styles.toggleRowIcon}
                style={{ width: 24, height: 24 }}
              />
              <span className={styles.toggleRowText}>
                Does this ride require any car seat or booster seat?
              </span>
            </div>
            <Radio.Group
              className={styles.radioGroup}
              value={values?.needsCarSeat ?? false}
              disabled={vehicleDisabled}
              onChange={(e) => handleNeedsCarSeatChange(e.target.value)}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </div>
          {touched?.needsCarSeat && fieldError(errors, 'needsCarSeat') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'needsCarSeat')}
            </div>
          )}

          {showCarBoosterComp && (
            <>
              <div className={styles.seatSectionHeader}>
                <FieldLabel required>Select the seat type</FieldLabel>
                <span>(Max Available Seats: {Math.max(0, 2 - totalSeats)} of 2)</span>
              </div>
              <SeatTable
                title="Car Seat"
                riderCount={values?.carSeatByRider || 0}
                driverCount={values?.carSeatByDriver || 0}
                vehicleDisabled={vehicleDisabled}
                totalSeats={totalSeats}
                onRiderChange={(n) => handleSeatCountChange('carSeatByRider', n)}
                onDriverChange={(n) => handleSeatCountChange('carSeatByDriver', n)}
              />
              <SeatTable
                title="Booster Seat"
                riderCount={values?.boosterSeatByRider || 0}
                driverCount={values?.boosterSeatByDriver || 0}
                vehicleDisabled={vehicleDisabled}
                totalSeats={totalSeats}
                onRiderChange={(n) => handleSeatCountChange('boosterSeatByRider', n)}
                onDriverChange={(n) => handleSeatCountChange('boosterSeatByDriver', n)}
              />
              {showSeatTypeValidationError && (
                <InfoAlert message={`*${CAR_SEAT_OR_BOOSTER_VALIDATION_MESSAGE}`} />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default memo(TripDetailsPanel);
